import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { Maybe } from '@/lib/types';

import { ReactComponent as CloseSvg } from '@/assets/icons/close-white.svg';
import { ReactComponent as ExpandSvg } from '@/assets/icons/expand.svg';
import { ReactComponent as MuteSvg } from '@/assets/icons/mute.svg';
import { ReactComponent as MutedSvg } from '@/assets/icons/muted.svg';
import { ReactComponent as PauseSvg } from '@/assets/icons/pause.svg';
import { ReactComponent as PlaySvg } from '@/assets/icons/play.svg';
import { ReactComponent as ShrinkSvg } from '@/assets/icons/shrink.svg';

import * as S from './VideoPlayer.styles';
interface VideoProps {
  mediaUrl: Maybe<string>;
  onClose?: () => void;
  poster?: string;
  title?: Maybe<string>;
}
export const VideoPlayer = ({
  mediaUrl,
  onClose,
  poster,
  title,
}: VideoProps) => {
  const { sendEvent } = useGoogleAnalytics();
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [percent, setPercent] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLProgressElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.load();
  }, [mediaUrl]);

  useEffect(() => {
    let hideControlsTimeout: NodeJS.Timeout;

    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(hideControlsTimeout);

      hideControlsTimeout = setTimeout(() => {
        setShowControls(false);
      }, 5000);
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(video.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoRef.current]);

  useEffect(() => {
    const video = videoRef.current;
    const progressBar = progressBarRef.current;
    if (!video || !progressBar) {
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      if (isSeeking) {
        const progressBarRect = progressBar.getBoundingClientRect();
        const offsetX = e.clientX - progressBarRect.left;
        const percent = offsetX / progressBar.clientWidth;
        const newTime = percent * duration;

        video.currentTime = newTime;
      }
    };

    const handleMouseUp = () => {
      setIsSeeking(false);
    };

    progressBar.addEventListener('mousemove', handleMouseMove);
    progressBar.addEventListener('mouseup', handleMouseUp);

    return () => {
      progressBar.removeEventListener('mousemove', handleMouseMove);
      progressBar.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isSeeking]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
      if (percent === 0 || percent === 100) {
        sendEvent({
          event: EVENT_NAME.VIDEO_START,
          video_title: title as string,
          video_url: mediaUrl as string,
        });
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };
  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };
  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    const element = document.documentElement;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen().then(() => {
          setIsFullscreen(true);
          document.body.style.overflow = 'hidden';
        });
      }
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        document.body.style.overflow = 'auto';
      });
    }
  };

  const handleCloseClick = () => {
    const isFullscreen = !!document.fullscreenElement;

    if (isFullscreen) {
      document.exitFullscreen().then(() => {
        onClose && onClose();
        document.body.style.overflow = 'auto';
      });
    } else {
      onClose && onClose();
      document.body.style.overflow = 'auto';
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLProgressElement>) => {
    const progressBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX;
    const percent = clickPosition / progressBar.clientWidth;
    const newTime = percent * duration;

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const handleProgressMouseDown = () => {
    setIsSeeking(true);
  };

  const handleProgressMouseUp = () => {
    setIsSeeking(false);
  };

  useEffect(() => {
    setPercent(Math.floor((currentTime / duration) * 100));
  }, [duration, currentTime]);

  useEffect(() => {
    const videoPercents = [25, 50, 75];
    if (videoPercents?.includes(percent)) {
      sendEvent({
        event: EVENT_NAME.VIDEO_PROGRESS,
        video_title: title as string,
        video_url: mediaUrl as string,
        video_percent: percent,
      });
    }
    if (percent === 100) {
      sendEvent({
        event: EVENT_NAME.VIDEO_COMPLETE,
        video_title: title as string,
      });
    }
  }, [percent]);

  const modalTargetElement = useRef<HTMLElement | null>(null);
  useEffect(() => {
    modalTargetElement.current = document.getElementById('modal-root');
  });

  return (
    <>
      {mediaUrl &&
        modalTargetElement.current &&
        createPortal(
          <S.VideoModal data-testid='VideoVideoModal'>
            <S.PlayerWrapper data-testid='VideoPlayerWrapper'>
              {showControls && (
                <S.CloseButton
                  data-testid='VideoCloseButton'
                  onClick={handleCloseClick}
                >
                  <CloseSvg />
                </S.CloseButton>
              )}
              <S.PlayerVideo
                ref={videoRef}
                autoPlay
                data-testid='VideoPlayerVideo'
                onClick={togglePlayPause}
                playsInline
                poster={poster}
              >
                <source src={mediaUrl} type='video/mp4' />
                <source src={mediaUrl} type='video/webm' />
              </S.PlayerVideo>
              {showControls && (
                <S.PlayerControls data-testid='VideoPlayerControls'>
                  <S.StyledPlayAndPauseButton
                    data-testid='VideoPlayAndPauseButton'
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? (
                      <PauseSvg data-testid='VideoPauseSvg' />
                    ) : (
                      <PlaySvg />
                    )}
                  </S.StyledPlayAndPauseButton>
                  <S.PlayersControlsProgressBar data-testid='VideoPlayersControlsProgressBar'>
                    <S.StyledProgress
                      ref={progressBarRef}
                      max={duration}
                      onClick={handleProgressClick}
                      onMouseDown={handleProgressMouseDown}
                      onMouseUp={handleProgressMouseUp}
                      value={currentTime}
                    />
                  </S.PlayersControlsProgressBar>
                  <S.StyledMuteButton onClick={toggleMute}>
                    {isMuted ? <MutedSvg /> : <MuteSvg />}
                  </S.StyledMuteButton>
                  <S.StyledToggleScreenButton
                    data-testid='VideoToggleFullscreenButton'
                    onClick={toggleFullscreen}
                  >
                    {isFullscreen ? <ShrinkSvg /> : <ExpandSvg />}
                  </S.StyledToggleScreenButton>
                </S.PlayerControls>
              )}
            </S.PlayerWrapper>
          </S.VideoModal>,
          modalTargetElement.current,
        )}
    </>
  );
};
